import React, { useState, useEffect } from "react";
import "react-date-range/dist/styles.css"; // main styles
import "react-date-range/dist/theme/default.css"; // theme styles
import { useTheme } from "@mui/material";
import { addDays } from "date-fns";
import { BiLoaderAlt } from "react-icons/bi";
import { Link } from "react-router-dom";
import { calendarToday, swapVert1 } from "../assets";
import BuyNodeMessage from "../pages/Node/components/BuyNodeMessage";
import NoNodesMessage from "../pages/Node/components/NoNodesMessage";
import TableHead from "../pages/Node/components/TableHead";
import { transactionsHistory } from "../pages/Node/constants";
import cn from "classnames";

const NodeTable = ({
  activeTab,
  connectedWalletAddress,
  transactions,
  hasNodes,
}) => {
  const [nodesData, setNodesData] = useState([]);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      key: "selection",
    },
  ]);
  const [showPicker, setShowPicker] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
		setNodesData(transactions);
    if (transactions && transactions.length > 0) {
      setIsLoading(false);
    }
  }, [transactions]);

  const handleSelect = (ranges) => {
    setDateRange([ranges.selection]);
    setShowPicker(false); // Close picker after selection
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedNodes = nodesData.sort((a, b) => {
    if (!sortConfig.key) return 0;
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? 1 : -1;
    }
    return 0;
  });

  const theme = useTheme();
  const capitalizeFirstLetter = (string) =>
    string.charAt(0).toUpperCase() + string.slice(1);

  const shortenAddress = (address) => {
    if (!address) return "";
    return `${address.substring(0, 15)}...${address.substring(
      address.length - 4
    )}`;
  };
  const mobileShortenAddress = (address) => {
    if (!address) return "";
    return `${address.substring(0, 5)}...${address.substring(
      address.length - 4
    )}`;
  };
  const formatTimeAgo = (timestamp) => {
    const timeDifference =
      Date.now() - new Date(parseInt(timestamp) * 1000).getTime();
    const minutes = Math.floor(timeDifference / (60 * 1000));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h ago`;
    if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
    return `${minutes}m ago`;
  };

  const sortById = () => {
    requestSort("blockNumber");
  };

  const sortByDate = () => {
    setShowPicker(!showPicker);
  };

  const sortByNodeCount = () => {
    requestSort("nodeIds");
  };

  const sortByNodeName = () => {
    requestSort("nodeIds");
  };

  const sortByTXHash = () => {
    requestSort("tx_hash");
  };

  return (
    <div
      className={cn("overflow-y-hidden overflow-y-scroll hide-scrollbar")}
      style={{ height: "calc(100% - 80px)" }}
    >
      <table
        className={`w-full hide-scrollbar h-full overflow-y-hidden ${
          isLoading ? "opacity-50" : ""
        }`}
      >
        <thead
          className={
            "sticky top-0 border-b border-lightGrayBlue dark:border-darkSlateBlue bg-white dark:bg-darkCardBg z-10"
          }
        >
          <tr className="grid grid-cols-12 w-full">
            <TableHead
              thName={"ID"}
              sortData={sortById}
              classes="col-span-4 md:col-span-2"
            />
            <TableHead
              thName={"Date"}
              sortData={sortByDate}
              classes={"col-span-2 hidden md:block"}
            />
            <TableHead
              thName={"Node count"}
              sortData={sortByNodeCount}
              classes={"col-span-2 hidden md:block"}
            />
            <TableHead
              thName={"Node name"}
              sortData={sortByNodeName}
              classes={"col-span-4 md:col-span-3"}
            />
            <TableHead
              thName={"TX hash"}
              sortData={sortByTXHash}
              classes={"col-span-4 md:col-span-3"}
            />
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr className="w-full flex justify-center items-center">
              <td colSpan="5" className="flex justify-center items-center">
                <BiLoaderAlt
                  className="animate-spin"
                  size={50}
                  color={theme.palette.mode === "light" ? "#000" : "#fff"}
                />
              </td>
            </tr>
          )}
          {!isLoading && sortedNodes.length > 0 ? (
            sortedNodes.map((row) => (
              <tr
                key={row.blockNumber}
                className={
                  "grid grid-cols-12 w-full border-b border-lightGrayBlue dark:border-darkSlateBlue"
                }
              >
                <td className="col-span-4 md:col-span-2 py-5">
                  <span className="px-3 inline-block	text-xs leading-6 text bg-center rounded-lg bg-lightBlue dark:bg-[#302E47] text-semiBlack dark:text-semiWhite h-6">
                    {row.blockNumber}
                  </span>
                </td>
                <td className="col-span-2 py-5  text-sm leading-6 text-black dark:text-white hidden md:block">
                  {formatTimeAgo(row.timeStamp)}
                </td>
                <td className="col-span-2 py-5  text-sm leading-6 text-black dark:text-white hidden md:block">
                  {row.nodeIds.length}
                </td>
                <td className="col-span-4 md:col-span-3 py-5 text-xs md:text-sm leading-6 text-black dark:text-white">
                  {`Node ${row.nodeIds.join(", ")}`}
                </td>
                <td className="col-span-4 md:col-span-3 py-5 text-xs md:text-sm leading-6 text-black dark:text-white">
                  <Link
                    to={`https://polygonscan.com/tx/${row.tx_hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="hidden xl:block">
                      {shortenAddress(row.tx_hash)}
                    </span>
                    <span className="block xl:hidden">
                      {mobileShortenAddress(row.tx_hash)}
                    </span>
                  </Link>
                </td>
              </tr>
            ))
          ) : hasNodes ? (
            <NoNodesMessage message={"transactions history"} />
          ) : (
            <BuyNodeMessage messageText={"transactions"} />
          )}
        </tbody>
      </table>
    </div>
  );
};

export default NodeTable;
